import { type CSSProperties, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import classNames from "classnames";

import { LoadingGroup } from "../../../general/LoadingGroup";
import { useId } from "../../../hooks/useId";
import { InputGroup, useInputDisabled, useInputReadonly } from "../../InputGroup";
import { useSubmitting } from "../../SubmitGroup";
import { useValidity } from "../../ValidityGroup";
import { InputContainer } from "../../shared";
import type { RTEActiveFormatDefs, RTEDefs } from "../RichTextEditorProps";
import { EDITOR_NODES, EDITOR_THEME } from "../configs/editorConfig";
import { ActiveFormatsPlugin, EMPTY_ACTIVE_FORMATS } from "../plugins/ActiveFormatsPlugin";
import { CollectLinksPlugin } from "../plugins/CollectLinksPlugin";
import { HydrateInitialValuePlugin } from "../plugins/HydrateInitialValuePlugin";
import { LinkClickPlugin } from "../plugins/LinkClickPlugin";
import { SerializeOnChangePlugin } from "../plugins/SerializeOnChangePlugin";
import { getBaseDefsFromHtmlProps } from "../utils/getBaseDefsFromHtmlProps";
import { getHtmlDefsFromBaseProps } from "../utils/getHtmlDefsFromBaseProps";
import { loadRecentUrls, promoteRecentUrl, removeRecentUrl, saveRecentUrls } from "../utils/recentUrls";
import { Editor } from "./Lexical";
import { Loader } from "./Loader";
import { Output } from "./Output";
import { Popover } from "./Popover";
import { Toolbar } from "./Toolbar";

import CSS from "../styles.module.css";

export const Input = forwardRef<HTMLDivElement, RTEDefs>(function Input(props, ref) {
  const {
    label,
    instructions,
    tooltip,
    hideLabel,
    defaultValue = null,
    required = false,
    autoFocus,
    lines,
    placeholder,
    serializeAs = "html",
    showOutput = false,
    outputLabel,
    resize = true,
    onChange,
    onFocusEnter,
    onFocusExit,
    value = defaultValue,
  } = props;

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();
  const [focused, setFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState<RTEActiveFormatDefs>(EMPTY_ACTIVE_FORMATS);
  const [popoverUrl, setPopoverUrl] = useState<string | null>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [recentUrls, setRecentUrls] = useState<readonly string[]>(loadRecentUrls);
  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const [serializedOutput, setSerializedOutput] = useState<string | null>(value ?? null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const inertContent = readOnly || submitting;
  const editable = !disabled && !inertContent;
  const baseHtmlDefs = getHtmlDefsFromBaseProps(props, {
    className: ["rich-text-editor", { "fixed-height": lines != null }],
    testIdPrefix: "rich-text-editor",
  });

  const handleChange = useCallback(
    (serialized: string) => {
      if (showOutput) setSerializedOutput(serialized);
      onChange?.(serialized, valueRef.current ?? "");
    },
    [onChange, showOutput],
  );

  useEffect(() => {
    if (showOutput) setSerializedOutput(value ?? null);
  }, [showOutput, value]);

  const handleFocusEnter = useCallback(() => {
    setFocused(true);
    onFocusEnter?.();
  }, [onFocusEnter]);

  const handleFocusExit = useCallback(() => {
    setFocused(false);
    onFocusExit?.();
  }, [onFocusExit]);

  const togglePopover = useCallback(() => {
    return setShowPopover((prev) => !prev);
  }, []);

  const openPopoverForLink = useCallback((url: string) => {
    setPopoverUrl(url);
    setShowPopover(true);
  }, []);

  const closePopover = useCallback(() => {
    setShowPopover(false);
    linkButtonRef.current?.focus();
  }, []);

  const recordRecentUrl = useCallback((rawUrl: string) => {
    setRecentUrls((prev) => promoteRecentUrl(prev, rawUrl));
  }, []);

  const handleRemoveRecentUrl = useCallback((url: string) => {
    setRecentUrls((prev) => removeRecentUrl(prev, url));
  }, []);

  useEffect(() => {
    saveRecentUrls(recentUrls);
  }, [recentUrls]);

  const initialConfig = useMemo(
    () => ({
      namespace: `rte-${id}`,
      theme: EDITOR_THEME,
      nodes: EDITOR_NODES,
      editable,
      onError: (error: Error) => console.error("[RichTextEditor] Lexical error:", error),
    }),
    [id, editable],
  );

  const contentAreaStyle = lines != null ? ({ "--rte-lines": lines } as CSSProperties) : undefined;
  return (
    <LoadingGroup.Placeholder
      fallback={<Loader className={props.className} style={props.style} lines={lines} hasInstructions={!!instructions} />}
    >
      <InputContainer
        {...getBaseDefsFromHtmlProps(baseHtmlDefs)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideLabel={hideLabel}
      >
        <InputGroup disabled={disabled} readOnly={readOnly}>
          <div
            ref={ref}
            className={classNames(CSS.RichTextEditor, {
              [CSS.focused]: focused && editable,
              [CSS.invalid]: invalid,
              [CSS.disabled]: disabled,
              [CSS.readonly]: inertContent,
            })}
            onFocus={handleFocusEnter}
            onBlur={handleFocusExit}
          >
            <LexicalComposer initialConfig={initialConfig}>
              {editable && (
                <Toolbar
                  activeFormats={activeFormats}
                  disabled={submitting}
                  onLinkButtonClick={togglePopover}
                  linkButtonRef={linkButtonRef}
                />
              )}

              <div className={classNames(CSS.ContentArea, { [CSS.resizable]: resize })} style={contentAreaStyle}>
                <RichTextPlugin
                  contentEditable={
                    <Editor
                      id={`${id}-input`}
                      ariaLabel={typeof label === "string" ? label : undefined}
                      ariaLabelledBy={label ? `${id}-label` : undefined}
                      ariaDescribedBy={instructions ? `${id}-instructions` : undefined}
                      ariaErrorMessage={invalid ? `${id}-error` : undefined}
                      ariaInvalid={invalid}
                      ariaRequired={disabled || readOnly || !required ? undefined : true}
                      ariaDisabled={disabled || submitting}
                      ariaReadOnly={inertContent}
                      ariaBusy={submitting}
                      tabIndex={editable ? 0 : -1}
                      placeholder={placeholder}
                    />
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
                {showPopover && editable && (
                  <Popover
                    initialUrl={popoverUrl}
                    onClose={closePopover}
                    recentUrls={recentUrls}
                    onRecordUrl={recordRecentUrl}
                    onRemoveUrl={handleRemoveRecentUrl}
                    anchorRef={linkButtonRef}
                  />
                )}
              </div>

              <HistoryPlugin />
              <ListPlugin />
              <LinkPlugin />
              {autoFocus && <AutoFocusPlugin />}
              <HydrateInitialValuePlugin initialValue={value} serializeAs={serializeAs} />
              <SerializeOnChangePlugin serializeAs={serializeAs} onChange={handleChange} />
              <ActiveFormatsPlugin onFormatsChange={setActiveFormats} onLinkEditRequest={setPopoverUrl} />
              <CollectLinksPlugin onLinkFound={recordRecentUrl} />
              {editable && <LinkClickPlugin onLinkClick={openPopoverForLink} />}
            </LexicalComposer>
          </div>
        </InputGroup>
        {showOutput && <Output value={serializedOutput} serializeAs={serializeAs} label={outputLabel} />}
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
